import React, { forwardRef, useRef, type ReactNode } from 'react';

import { FloatingFocusManager, FloatingPortal, useTransitionStyles } from '@floating-ui/react';
import classnames from 'classnames';
import { isNil } from 'ramda';

import { useMergeRefs } from '../../../../hooks/useMergeRefs';
import { Key } from '../../../../utils';
import { combineEventHandlers } from '../../../Floating/Floating.utils';
import { useFloatingPortalRoot } from '../../../Floating/useFloatingPortalRoot';
import { useSelectRootContext } from '../root/SelectRootContext';
import { useSelectRootFloatingContext } from '../root/SelectRootFloatingContext';

import { useCloseOnFocusOutside } from './useCloseOnFocusOutside';

import styles from './SelectDropdown.module.scss';

export type SelectDropdownProps = React.ComponentPropsWithoutRef<'div'> & {
    getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
    className?: string;
    children?: ReactNode;
};

export const SelectDropdown = forwardRef<HTMLDivElement, SelectDropdownProps>(
    ({ getPopupContainer, className, children, style, ...rest }: SelectDropdownProps, ref) => {
        useCloseOnFocusOutside();
        const hasBeenOpened = useRef(false);
        const {
            disabled,
            readOnly,
            isInlineInputSearchVisible,
            isTypingRef,
            setPointer,
            selection: { selectOptionByIndex },
            options: { activeIndex, initialFocusIndex },
            id,
            open,
            destroyOnHide,
            animationDuration
        } = useSelectRootContext();

        if (open) {
            hasBeenOpened.current = true;
        }

        const {
            floatingRefs,
            computedFloatingStyles,
            focusManagerContext,
            floatingInteractions: { getFloatingProps }
        } = useSelectRootFloatingContext();

        const { isMounted, styles: transitionStyles } = useTransitionStyles(focusManagerContext, {
            duration: animationDuration,
            initial: {
                transform: 'scale(0.9)',
                opacity: 0
            },
            common: ({ side }) => ({
                transitionProperty: 'opacity, transform',
                transitionDuration: `${animationDuration}ms`,
                transformOrigin: {
                    top: 'bottom',
                    bottom: 'top',
                    left: 'right',
                    right: 'left'
                }[side],
                willChange: 'opacity, transform'
            }),
            open: {
                opacity: 1,
                transform: 'scale(1)'
            },
            close: {
                opacity: 0,
                transform: 'scale(0.9)'
            }
        });

        const floatingProps =
            disabled || readOnly
                ? {}
                : getFloatingProps({
                      onKeyDown(e) {
                          setPointer(false);

                          if (e.key === Key.Enter && !isNil(activeIndex)) {
                              e.preventDefault();
                              selectOptionByIndex(activeIndex);
                          }

                          if (e.key === Key.Space && !isTypingRef.current) {
                              e.preventDefault();
                          }
                      },
                      onPointerMove() {
                          setPointer(true);
                      },
                      'aria-labelledby': id,
                      style: {
                          ...style,
                          ...computedFloatingStyles,
                          ...(!isMounted &&
                              !destroyOnHide && {
                                  display: 'none',
                                  pointerEvents: 'none'
                              })
                      }
                  });

        const floatingPortalRoot = useFloatingPortalRoot({
            getPopupContainer,
            refs: floatingRefs
        });

        const mergedRefs = useMergeRefs([floatingRefs.setFloating, ref]);

        if (!isMounted && destroyOnHide) return null;
        if (!isMounted && !hasBeenOpened.current) return null;

        return (
            <FloatingPortal root={floatingPortalRoot}>
                <FloatingFocusManager
                    disabled={!isMounted}
                    context={focusManagerContext}
                    modal={false}
                    initialFocus={isInlineInputSearchVisible ? -1 : initialFocusIndex}
                >
                    <div
                        ref={mergedRefs}
                        role="presentation"
                        className={classnames(styles.container, className)}
                        {...{
                            ...rest,
                            ...floatingProps,
                            ...combineEventHandlers(rest, floatingProps)
                        }}
                    >
                        <div className={styles.content} style={transitionStyles}>
                            {children}
                        </div>
                    </div>
                </FloatingFocusManager>
            </FloatingPortal>
        );
    }
);

SelectDropdown.displayName = 'SelectDropdown';
